import React, { useState, useMemo, useEffect } from 'react';
import { Document, Page, Text, View, StyleSheet, Font, pdf } from '@react-pdf/renderer';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { downloadPDF } from '../../utils/pdfUtils';

// Register font
Font.register({
  family: 'Roboto',
  src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf'
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Roboto',
    fontSize: 12,
  },
  headerContainer: {
    marginBottom: 20,
    borderBottom: 2,
    borderBottomColor: '#333',
    paddingBottom: 10,
  },
  businessHeader: {
    fontSize: 20,
    textAlign: 'center',
    marginBottom: 8,
    color: '#1a365d',
    fontWeight: 'bold',
  },
  businessInfo: {
    fontSize: 10,
    textAlign: 'center',
    color: '#4a5568',
    marginBottom: 4,
  },
  receiptTitle: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#2563eb',
  },
  summarySection: {
    marginBottom: 20,
    padding: 10,
    backgroundColor: '#f8fafc',
  },
  summaryTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1e293b',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  summaryLabel: {
    color: '#475569',
  },
  summaryValue: {
    fontWeight: 'bold',
    color: '#1e293b',
  },
  ordersSection: {
    marginTop: 20,
  },
  ordersSectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1e293b',
  },
  orderSection: {
    marginBottom: 15,
    borderBottom: 1,
    borderBottomColor: '#e2e8f0',
    paddingBottom: 10,
  },
  orderHeader: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    padding: 5,
    marginBottom: 5,
  },
  orderHeaderCell: {
    fontWeight: 'bold',
    color: '#475569',
    fontSize: 10,
  },
  orderRow: {
    flexDirection: 'row',
    padding: 5,
  },
  orderCell: {
    fontSize: 10,
    color: '#1e293b',
  },
  itemsTable: {
    marginTop: 10,
    marginLeft: 10,
  },
  itemsHeader: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    padding: 5,
    marginBottom: 5,
  },
  itemHeaderCell: {
    fontWeight: 'bold',
    color: '#64748b',
    fontSize: 9,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: 8,
  },
});

const PurchaseReport = ({ purchases, dateRange, businessDetails, supplierFilter }) => {
  const filteredPurchases = useMemo(() => {
    if (!purchases || !Array.isArray(purchases)) return [];
    
    let filtered = purchases.filter(purchase => 
      purchase && 
      purchase.supplierName &&
      purchase.totalAmount &&
      purchase.amountPaid !== undefined &&
      purchase.remainingBalance !== undefined
    );
    
    if (dateRange?.startDate && dateRange?.endDate) {
      filtered = filtered.filter(purchase => {
        const purchaseDate = purchase.createdAt ? new Date(purchase.createdAt) 
          : purchase.date?.seconds ? new Date(purchase.date.seconds * 1000)
          : new Date(purchase.date);
          
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        purchaseDate.setHours(0, 0, 0, 0);

        return purchaseDate >= startDate && purchaseDate <= endDate;
      });
    }
    
    if (supplierFilter) {
      filtered = filtered.filter(purchase => 
        purchase.supplierName === supplierFilter
      );
    }
    
    return filtered.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) 
        : a.date?.seconds ? new Date(a.date.seconds * 1000)
        : new Date(a.date);
      const dateB = b.createdAt ? new Date(b.createdAt)
        : b.date?.seconds ? new Date(b.date.seconds * 1000)
        : new Date(b.date);
      return dateA - dateB;
    });
  }, [purchases, dateRange, supplierFilter]);

  if (!purchases?.length || !dateRange || !businessDetails) {
    return null;
  }

  if (!filteredPurchases.length) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <View style={styles.headerContainer}>
            <Text style={styles.businessHeader}>{businessDetails?.name}</Text>
            <Text style={styles.businessInfo}>{businessDetails?.address}</Text>
            <Text style={styles.businessInfo}>
              Phone: {businessDetails?.phone} | Email: {businessDetails?.email}
            </Text>
            <Text style={styles.businessInfo}>GST: {businessDetails?.gstNumber}</Text>
          </View>

          <Text style={styles.receiptTitle}>
            {supplierFilter ? `Purchase Report - ${supplierFilter}` : 'Purchase Report'}
          </Text>

          <View style={styles.summarySection}>
            <Text style={styles.summaryTitle}>Report Summary</Text>
            <Text style={styles.summaryRow}>
              Period: {dateRange.startDate?.toLocaleDateString()} to {dateRange.endDate?.toLocaleDateString()}
            </Text>
          </View>

          <View style={[styles.summarySection, { alignItems: 'center', justifyContent: 'center' }]}>
            <Text style={{ color: '#64748b', fontSize: 14 }}>
              No purchases found for the selected date range
            </Text>
          </View>

          <Text style={styles.footer}>
            Generated on {new Date().toLocaleString()} | {businessDetails?.name}
          </Text>
        </Page>
      </Document>
    );
  }

  const totalPurchases = filteredPurchases.reduce((sum, purchase) => sum + (Number(purchase.totalAmount) || 0), 0);
  const totalPaid = filteredPurchases.reduce((sum, purchase) => sum + (Number(purchase.amountPaid) || 0), 0);
  const totalPending = filteredPurchases.reduce((sum, purchase) => sum + (Number(purchase.remainingBalance) || 0), 0);

  const columnFlex = supplierFilter ? {
    date: 1.5,
    purchaseId: 2,
    total: 1.5,
    paid: 1.5,
    balance: 1.5
  } : {
    date: 1,
    purchaseId: 1.5,
    supplier: 2,
    total: 1,
    paid: 1,
    balance: 1
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerContainer}>
          <Text style={styles.businessHeader}>{businessDetails?.name}</Text>
          <Text style={styles.businessInfo}>{businessDetails?.address}</Text>
          <Text style={styles.businessInfo}>
            Phone: {businessDetails?.phone} | Email: {businessDetails?.email}
          </Text>
          <Text style={styles.businessInfo}>GST: {businessDetails?.gstNumber}</Text>
        </View>

        <Text style={styles.receiptTitle}>
          {supplierFilter ? `Purchase Report - ${supplierFilter}` : 'Purchase Report'}
        </Text>

        <View style={styles.summarySection}>
          <Text style={styles.summaryTitle}>Report Summary</Text>
          <Text style={styles.summaryRow}>
            Period: {dateRange.startDate?.toLocaleDateString()} to {dateRange.endDate?.toLocaleDateString()}
          </Text>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Purchases:</Text>
            <Text style={styles.summaryValue}>{filteredPurchases.length}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Amount:</Text>
            <Text style={styles.summaryValue}>Rs. {totalPurchases.toLocaleString()}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Amount Paid:</Text>
            <Text style={styles.summaryValue}>Rs. {totalPaid.toLocaleString()}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Pending Amount:</Text>
            <Text style={styles.summaryValue}>Rs. {totalPending.toLocaleString()}</Text>
          </View>
        </View>

        <View style={styles.ordersSection}>
          {filteredPurchases.map((purchase, index) => (
            <View key={purchase.id} style={styles.orderSection}>
              <View style={styles.orderHeader}>
                <Text style={[styles.orderHeaderCell, { flex: columnFlex.date }]}>Date</Text>
                <Text style={[styles.orderHeaderCell, { flex: columnFlex.purchaseId }]}>Purchase ID</Text>
                {!supplierFilter && (
                  <Text style={[styles.orderHeaderCell, { flex: columnFlex.supplier }]}>Supplier</Text>
                )}
                <Text style={[styles.orderHeaderCell, { flex: columnFlex.total }]}>Total</Text>
                <Text style={[styles.orderHeaderCell, { flex: columnFlex.paid }]}>Paid</Text>
                <Text style={[styles.orderHeaderCell, { flex: columnFlex.balance }]}>Balance</Text>
              </View>

              <View style={styles.orderRow}>
                <Text style={[styles.orderCell, { flex: columnFlex.date }]}>
                  {purchase.createdAt 
                    ? new Date(purchase.createdAt).toLocaleDateString()
                    : purchase.date?.seconds 
                      ? new Date(purchase.date.seconds * 1000).toLocaleDateString()
                      : new Date(purchase.date).toLocaleDateString()}
                </Text>
                <Text style={[styles.orderCell, { flex: columnFlex.purchaseId }]}>{purchase.id}</Text>
                {!supplierFilter && (
                  <Text style={[styles.orderCell, { flex: columnFlex.supplier }]}>{purchase.supplierName}</Text>
                )}
                <Text style={[styles.orderCell, { flex: columnFlex.total }]}>
                  Rs. {purchase.totalAmount?.toLocaleString()}
                </Text>
                <Text style={[styles.orderCell, { flex: columnFlex.paid }]}>
                  Rs. {purchase.amountPaid?.toLocaleString()}
                </Text>
                <Text style={[styles.orderCell, { flex: columnFlex.balance }]}>
                  Rs. {purchase.remainingBalance?.toLocaleString()}
                </Text>
              </View>

              <View style={styles.itemsTable}>
                <View style={styles.itemsHeader}>
                  <Text style={[styles.itemHeaderCell, { flex: 2 }]}>Item</Text>
                  <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Quantity</Text>
                  <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Price/kg</Text>
                  <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Amount</Text>
                </View>

                {purchase.items?.map((item, itemIndex) => (
                  <View key={itemIndex} style={styles.orderRow}>
                    <Text style={[styles.orderCell, { flex: 2 }]}>{item.vegetable}</Text>
                    <Text style={[styles.orderCell, { flex: 1 }]}>{item.quantity} kg</Text>
                    <Text style={[styles.orderCell, { flex: 1 }]}>Rs. {item.pricePerKg}</Text>
                    <Text style={[styles.orderCell, { flex: 1 }]}>
                      Rs. {(item.quantity * item.pricePerKg).toLocaleString()}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          ))}
        </View>

        <Text style={styles.footer}>
          Generated on {new Date().toLocaleString()} | {businessDetails?.name}
        </Text>
      </Page>
    </Document>
  );
};

export const PDFDownloadButton = ({ purchases, dateRange, businessDetails, supplierFilter }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  if (!purchases || !dateRange.startDate || !dateRange.endDate || !businessDetails) {
    return null;
  }

  const handleDownload = async () => {
    setIsGenerating(true);
    try {
      const doc = (
        <PurchaseReport
          purchases={purchases}
          dateRange={dateRange}
          businessDetails={businessDetails}
          supplierFilter={supplierFilter}
        />
      );
      await downloadPDF(
        doc, 
        `purchase-report-${dateRange.startDate.toISOString().split('T')[0]}-to-${dateRange.endDate.toISOString().split('T')[0]}${supplierFilter ? `-${supplierFilter}` : ''}.pdf`
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <button
      onClick={handleDownload}
      disabled={isGenerating}
      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400"
    >
      <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
      {isGenerating ? 'Generating Report...' : 'Download Report'}
    </button>
  );
};

export default function PurchaseReports({ purchases }) {
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [businessDetails, setBusinessDetails] = useState(null);
  
  useEffect(() => {
    const fetchBusinessDetails = async () => {
      const docRef = doc(db, 'settings', 'business');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setBusinessDetails(docSnap.data());
      }
    };
    fetchBusinessDetails();
  }, []);

  const filteredPurchases = useMemo(() => {
    if (!purchases || !Array.isArray(purchases)) return [];
    
    let filtered = purchases.filter(purchase => 
      purchase && 
      purchase.supplierName &&
      purchase.totalAmount &&
      purchase.amountPaid !== undefined &&
      purchase.remainingBalance !== undefined
    );
    
    if (dateRange.startDate && dateRange.endDate) {
      filtered = filtered.filter(purchase => {
        const purchaseDate = purchase.createdAt ? new Date(purchase.createdAt) 
          : purchase.date?.seconds ? new Date(purchase.date.seconds * 1000)
          : new Date(purchase.date);
        
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        purchaseDate.setHours(0, 0, 0, 0);

        return purchaseDate >= startDate && purchaseDate <= endDate;
      });
    }
    
    if (selectedSupplier) {
      filtered = filtered.filter(purchase => 
        purchase.supplierName === selectedSupplier
      );
    }
    
    return filtered.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) 
        : a.date?.seconds ? new Date(a.date.seconds * 1000)
        : new Date(a.date);
      const dateB = b.createdAt ? new Date(b.createdAt)
        : b.date?.seconds ? new Date(b.date.seconds * 1000)
        : new Date(b.date);
      return dateA - dateB;
    });
  }, [purchases, dateRange, selectedSupplier]);

  return { PDFDownloadButton, filteredPurchases };
} 